import { useState, useRef, useEffect } from 'react'
import { Send, Loader2, User } from 'lucide-react'
import { motion } from 'framer-motion'
import { useChatStore } from '../../stores/chatStore'
import { useUIStore } from '../../stores/uiStore'
import type { ChatMessage } from '../../types'
import ResponsePanel from '../response/ResponsePanel'
import ConfirmDialog from '../ConfirmDialog'
import clsx from 'clsx'
import { formatDistanceToNow } from 'date-fns'

// Shellby's warm loading messages
const LOADING_MESSAGES = [
  "Reading your question...",
  "Hmm, let me think about this...",
  "Crafting the perfect response...",
  "Almost there...",
  "Making sure this is just right...",
]

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user'
  const { setActiveTab } = useUIStore()

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className={clsx(
        'flex gap-4 mb-6',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar */}
      {isUser ? (
        <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center glass-avatar-user">
          <User className="w-4 h-4" />
        </div>
      ) : (
        <img
          src="/shellby.png"
          alt="Shellby"
          className="flex-shrink-0 w-8 h-8 object-contain"
        />
      )}

      {/* Message content - glassmorphic card treatment for both */}
      <div
        className="max-w-[85%] sm:max-w-[80%] bg-[var(--luxury-glass-elevated)] backdrop-blur-[var(--glass-depth-elevated)] border border-white/25 shadow-[var(--shadow-glass-medium)] text-[var(--bbs-text-primary)]"
        style={{
          borderRadius: 'var(--bbs-radius-md)',
          padding: 'var(--bbs-spacing-lg) var(--bbs-spacing-xl)' // 24px 32px - more breathing room
        }}
      >
        <p className="whitespace-pre-wrap text-base sm:text-base leading-relaxed break-words">{message.content}</p>

        {/* Show response panel for assistant messages with final responses */}
        {!isUser && message.response && message.response.tweet && (
          <div className="mt-4 pt-4 border-t border-white/20">
            <p className="text-xs opacity-70 mb-2">Click a platform to view response:</p>
            <div className="flex gap-2">
              {(['twitter', 'facebook', 'forum'] as const).map((platform) => (
                <button
                  key={platform}
                  onClick={() => setActiveTab(platform)}
                  className="px-3 py-1.5 text-xs bg-white/20 hover:bg-white/30 transition-colors capitalize"
                  style={{ borderRadius: 'var(--bbs-radius-sm)' }}
                >
                  {platform === 'twitter' ? 'Twitter/X' : platform}
                </button>
              ))}
            </div>
          </div>
        )}

        <p className="text-xs opacity-50 mt-2">
          {formatDistanceToNow(new Date(message.timestamp), { addSuffix: true })}
        </p>
      </div>
    </motion.div>
  )
}

export default function ChatInterface() {
  const [input, setInput] = useState('')
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0)
  const [showClearConfirm, setShowClearConfirm] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const { messages, isLoading, sendMessage, clearChat } = useChatStore()

  // Rotate loading messages
  useEffect(() => {
    if (!isLoading) {
      setLoadingMessageIndex(0)
      return
    }
    const interval = setInterval(() => {
      setLoadingMessageIndex((i) => (i + 1) % LOADING_MESSAGES.length)
    }, 2000)
    return () => clearInterval(interval)
  }, [isLoading])

  // Get the latest response for the response panel
  const latestResponse = messages
    .filter((m) => m.role === 'assistant' && m.response)
    .pop()?.response

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!input.trim() || isLoading) return
    sendMessage(input)
    setInput('')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  // Auto-resize textarea
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value)
    // Reset height to auto to get the correct scrollHeight
    e.target.style.height = 'auto'
    // Set height to scrollHeight, capped at max-height
    e.target.style.height = `${Math.min(e.target.scrollHeight, 150)}px`
  }

  return (
    <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 min-h-[400px] h-[calc(100vh-12rem)]">
      {/* Chat panel */}
      <div className="flex-1 flex flex-col card p-0 overflow-hidden min-w-0">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 sm:p-8">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <img
                src="/shellby.png"
                alt="Shellby"
                className="w-24 h-24 object-contain mb-4"
              />
              <p className="text-[var(--bbs-text-secondary)] mb-2">
                Hi, I'm Shellby!
              </p>
              <p className="text-sm text-[var(--bbs-text-tertiary)] max-w-md">
                Tell me about a customer question and I'll help you craft the perfect response.
                I might ask a few questions to make sure I get it just right.
              </p>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <MessageBubble key={message.id} message={message} />
              ))}
              {isLoading && (
                <div className="flex gap-4 mb-6">
                  <img
                    src="/shellby.png"
                    alt="Shellby"
                    className="flex-shrink-0 w-8 h-8 object-contain animate-bounce [animation-duration:1.5s]"
                  />
                  <div
                    className="bg-[var(--luxury-glass-elevated)] backdrop-blur-[var(--glass-depth-elevated)] border border-white/25 shadow-[var(--shadow-glass-medium)]"
                    style={{
                      borderRadius: 'var(--bbs-radius-md)',
                      padding: 'var(--bbs-spacing-lg) var(--bbs-spacing-xl)' // Match message bubbles
                    }}
                  >
                    <p className="text-base text-[var(--bbs-text-secondary)]">{LOADING_MESSAGES[loadingMessageIndex]}</p>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input */}
        <form onSubmit={handleSubmit} className="p-4 border-t border-[var(--bbs-color-border)]">
          <div className="flex gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="What can I help you respond to today?"
              className="input flex-1 resize-none min-h-[48px] max-h-[150px] py-3 text-base leading-normal"
              rows={1}
              disabled={isLoading}
            />
            <button
              type="submit"
              className="btn-glass group flex items-center justify-center hover:scale-105 active:scale-95 disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-not-allowed"
              style={{ padding: 'var(--bbs-spacing-md) var(--bbs-spacing-xl)', borderRadius: 'var(--bbs-radius-md)' }}
              disabled={!input.trim() || isLoading}
            >
              {/* Icon with magenta glow */}
              <div className="relative">
                <div className="absolute inset-0 bg-[var(--bbs-synthwave-hot-pink)] blur-md opacity-50 group-hover:opacity-75 transition-opacity" />
                {isLoading ? (
                  <Loader2 className="relative w-5 h-5 text-[var(--bbs-synthwave-hot-pink)] drop-shadow-[0_0_6px_rgba(255,0,128,0.6)] animate-spin" />
                ) : (
                  <Send className="relative w-5 h-5 text-[var(--bbs-synthwave-hot-pink)] drop-shadow-[0_0_6px_rgba(255,0,128,0.6)]" strokeWidth={2} />
                )}
              </div>
            </button>
          </div>
          <p className="text-xs text-[var(--bbs-text-tertiary)] mt-2">
            Press Enter to send, Shift+Enter for new line
          </p>
        </form>
      </div>

      {/* Response panel (shows when we have a response) */}
      {latestResponse && (
        <div className="w-full lg:w-[400px] xl:w-[480px] 2xl:w-[560px] lg:flex-shrink-0 overflow-y-auto max-h-[50vh] lg:max-h-none">
          <ResponsePanel response={latestResponse} />
        </div>
      )}

      {/* Clear chat confirm */}
      <ConfirmDialog
        isOpen={showClearConfirm}
        title="Start a fresh conversation?"
        message="This will clear your current chat with Shellby. Your history will still be saved."
        confirmText="Start fresh"
        cancelText="Keep chatting"
        variant="warning"
        onConfirm={() => {
          clearChat()
          setShowClearConfirm(false)
        }}
        onCancel={() => setShowClearConfirm(false)}
      />
    </div>
  )
}
